import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { timeClockKey, timeClockQuery } from "@/lib/screen-queries";
import {
  Play, StopCircle, Coffee, Utensils, MapPin, Clock,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  clockIn, clockOut, startBreak, endBreak,
} from "@/lib/time-clock";
import { SosHoldBanner } from "@/components/SosHoldBanner";
import { PunchRequestPanel } from "@/components/PunchRequestPanel";
import { useLiveLocationConsent } from "@/lib/live-location-consent";
import { LiveLocationSelfCheck } from "@/components/LiveLocationSelfCheck";
import { NativeGpsRequiredNotice } from "@/components/NativeGpsRequiredNotice";
import { nativeGpsRequiredHere, useRequireNativeLiveGps } from "@/lib/native-gps-policy";
import { openLocationConsentScreen, useProfileLocationConsent } from "@/lib/location-consent-profile";

export const Route = createFileRoute("/_authenticated/time-clock")({
  component: TimeClockPage,
  head: () => ({ meta: [{ title: "Time Clock — MissionReady" }] }),
});

type Entry = {
  id: string;
  organization_id: string;
  officer_id: string;
  site_id: string | null;
  clock_in_at: string;
  clock_out_at: string | null;
  break_minutes: number;
};
type Brk = {
  id: string;
  time_entry_id: string;
  break_type: string;
  paid: boolean;
  started_at: string;
  ended_at: string | null;
};
type Site = { id: string; name: string };
type Flags = {
  time_clock_enabled: boolean;
  time_clock_geofence_required: boolean;
  time_clock_geofence_radius_m: number;
};
type Action = {
  id: string;
  label: string;
  at: string;
  kind: "shift_start" | "shift_end" | "lunch_start" | "lunch_end" | "meal_start" | "meal_end";
};

function TimeClockPage() {
  const { currentOrg, user } = useAuth();
  const [siteId, setSiteId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  // Live location sharing is the guard's own consent (asked once at first login);
  // the reporter in the authenticated shell acts on it while clocked in.
  const [locConsent, setLocConsent] = useLiveLocationConsent(user?.id ?? null);
  const liveLocation = locConsent === "granted";
  const requireNativeGps = useRequireNativeLiveGps(currentOrg?.id ?? null);
  // Account-level on-duty location disclosure: required before a first clock-in.
  const { status: locationConsent } = useProfileLocationConsent(user?.id ?? null);

  // Flags, sites, my entries and breaks share one cache entry with the
  // prefetcher, so arriving from a warmed link renders instantly.
  const qc = useQueryClient();
  const orgId = currentOrg?.id ?? "";
  const userId = user?.id ?? "";
  const { data: tcData } = useQuery(timeClockQuery(orgId, userId));
  const flags = (tcData?.flags ?? null) as Flags | null;
  const sites = (tcData?.sites ?? []) as Site[];
  const entries = (tcData?.entries ?? []) as Entry[];
  const breaks = (tcData?.breaks ?? []) as Brk[];
  const reload = async () => {
    if (!orgId || !userId) return;
    await qc.invalidateQueries({ queryKey: timeClockKey(orgId, userId) });
  };

  // Preselect the guard's site so punches are always attributed to a site
  // (an unattributed punch never shows up in Site Activity).
  useEffect(() => {
    if (!siteId && sites.length > 0) setSiteId(sites[0]!.id);
  }, [siteId, sites]);

  // Realtime punches/breaks refresh the cached data in the background.
  useEffect(() => {
    if (!orgId || !userId) return;
    const ch = supabase
      .channel(`time-clock-mine:${orgId}:${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "time_entries", filter: `officer_id=eq.${userId}` }, () => { void reload(); })
      .on("postgres_changes", { event: "*", schema: "public", table: "time_breaks", filter: `officer_id=eq.${userId}` }, () => { void reload(); })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, userId]);

  const myActive = useMemo(
    () => entries.find((e) => e.clock_out_at == null) ?? null,
    [entries],
  );
  const activeBreak = useMemo(() => {
    if (!myActive) return null;
    return breaks.find((b) => b.time_entry_id === myActive.id && b.ended_at == null) ?? null;
  }, [breaks, myActive]);

  // Distinguish "Lunch" (off-duty, unpaid) vs "On-Duty Meal" (paid)
  const onLunch = activeBreak ? activeBreak.paid === false : false;
  const onMeal = activeBreak ? activeBreak.paid === true : false;

  const onShift = !!myActive;
  const canStartShift = !onShift && locationConsent === "given";
  const canEndShift = onShift && !activeBreak;
  const canStartLunch = onShift && !activeBreak;
  const canEndLunch = onLunch;
  const canStartMeal = onShift && !activeBreak;
  const canEndMeal = onMeal;

  // Site for current/next punch
  const activeSiteId = myActive?.site_id ?? siteId ?? null;
  const siteName = (id: string | null) => sites.find((s) => s.id === id)?.name ?? "—";

  const doShiftStart = async () => {
    if (!currentOrg || !user || !flags) return;
    setBusy(true);
    try {
      await clockIn({
        organizationId: currentOrg.id,
        officerId: user.id,
        siteId: siteId || null,
        geofenceRequired: flags.time_clock_geofence_required,
        geofenceRadiusM: flags.time_clock_geofence_radius_m,
      });
      toast.success("Shift started");
    } catch (e: any) { toast.error(e.message ?? "Could not start shift"); }
    finally { setBusy(false); }
  };
  const doShiftEnd = async () => {
    if (!myActive || !flags) return;
    setBusy(true);
    try {
      await clockOut(myActive.id, flags.time_clock_geofence_required, flags.time_clock_geofence_radius_m, myActive.site_id);
      toast.success("Shift ended");
    } catch (e: any) { toast.error(e.message ?? "Could not end shift"); }
    finally { setBusy(false); }
  };
  const doLunchStart = async () => {
    if (!myActive || !currentOrg || !user) return;
    setBusy(true);
    try {
      await startBreak(myActive.id, currentOrg.id, user.id, "meal", false);
      toast.success("Lunch started — off duty");
    } catch (e: any) { toast.error(e.message ?? "Could not start lunch"); }
    finally { setBusy(false); }
  };
  const doMealStart = async () => {
    if (!myActive || !currentOrg || !user) return;
    setBusy(true);
    try {
      await startBreak(myActive.id, currentOrg.id, user.id, "meal", true);
      toast.success("On-duty meal started");
    } catch (e: any) { toast.error(e.message ?? "Could not start meal"); }
    finally { setBusy(false); }
  };
  const doBreakEnd = async (label: string) => {
    if (!activeBreak) return;
    setBusy(true);
    try {
      await endBreak(activeBreak.id);
      toast.success(`${label} ended`);
    } catch (e: any) { toast.error(e.message ?? "Could not end"); }
    finally { setBusy(false); }
  };

  // My last actions — derived from entries + breaks
  const lastActions: Action[] = useMemo(() => {
    const out: Action[] = [];
    entries.forEach((e) => {
      out.push({ id: `${e.id}-in`, kind: "shift_start", label: "Shift Start", at: e.clock_in_at });
      if (e.clock_out_at) out.push({ id: `${e.id}-out`, kind: "shift_end", label: "Shift End", at: e.clock_out_at });
    });
    breaks.forEach((b) => {
      const startKind = b.paid ? "meal_start" : "lunch_start";
      const endKind = b.paid ? "meal_end" : "lunch_end";
      const startLabel = b.paid ? "On-Duty Meal Start" : "Lunch Start";
      const endLabel = b.paid ? "On-Duty Meal End" : "Lunch End";
      out.push({ id: `${b.id}-bs`, kind: startKind as Action["kind"], label: startLabel, at: b.started_at });
      if (b.ended_at) out.push({ id: `${b.id}-be`, kind: endKind as Action["kind"], label: endLabel, at: b.ended_at });
    });
    return out.sort((a, b) => +new Date(b.at) - +new Date(a.at)).slice(0, 12);
  }, [entries, breaks]);

  if (flags && !flags.time_clock_enabled) {
    return (
      <div className="px-4 py-10 text-center text-muted-foreground">
        Time clock is disabled for this workspace.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:py-8">
      {/* SOS / Man Down banner */}
      <SosHoldBanner />

      {/* Header */}
      <div className="mt-6 flex items-center gap-2">
        <Clock className="h-5 w-5 text-primary" />
        <h1 className="font-display text-2xl font-bold md:text-3xl">Time Clock</h1>
      </div>

      {/* Site selector when off-shift */}
      {!onShift && (
        <div className="mt-4 grid gap-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#C0C0C0]">Site</label>
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger aria-label="Site"><SelectValue placeholder="Select site for this shift…" /></SelectTrigger>
            <SelectContent>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Consent gate — a first clock-in needs the on-duty location disclosure. */}
      {!onShift && locationConsent === "unset" && (
        <div className="mt-4 rounded-xl border border-[#E8C07C]/40 bg-[#E8C07C]/10 p-4">
          <div className="text-sm font-semibold text-foreground">
            Review location tracking before your first shift
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Shift Start stays unavailable until you read and agree to how location works
            while you are on duty.
          </p>
          <Button size="sm" className="mt-3" onClick={openLocationConsentScreen}>
            Review location consent
          </Button>
        </div>
      )}

      {/* 6-tile grid */}
      <div className="mt-5 grid grid-cols-2 gap-3 sm:gap-4">
        <PunchTile
          label="Shift Start" sub="Begin work" Icon={Play}
          tone="success" disabled={!canStartShift || busy}
          onClick={doShiftStart}
        />
        <PunchTile
          label="Shift End" sub="End work" Icon={StopCircle}
          tone="danger" disabled={!canEndShift || busy}
          onClick={doShiftEnd}
        />
        <PunchTile
          label="Lunch Start" sub="Off duty" Icon={Coffee}
          tone="muted" disabled={!canStartLunch || busy}
          onClick={doLunchStart}
        />
        <PunchTile
          label="Lunch End" sub="Resume" Icon={Coffee}
          tone="muted" disabled={!canEndLunch || busy}
          onClick={() => doBreakEnd("Lunch")}
        />
        <PunchTile
          label="On-Duty Meal Start" sub="Start" Icon={Utensils}
          tone="info" disabled={!canStartMeal || busy}
          onClick={doMealStart}
        />
        <PunchTile
          label="On-Duty Meal End" sub="End" Icon={Utensils}
          tone="info" disabled={!canEndMeal || busy}
          onClick={() => doBreakEnd("On-duty meal")}
        />
      </div>

      {/* On Duty / Live Location */}
      <section className="mt-6 rounded-xl border border-[#C0C0C0]/20 bg-card p-4">
        <div className="flex items-center gap-3">
          <MapPin className="h-5 w-5 text-primary" />
          <div className="flex-1">
            <div className="text-sm font-semibold">
              {onShift
                ? liveLocation ? "On Duty · Live Location on" : "On Duty · Live Location off"
                : "Off Duty"}
            </div>
            <div className="text-xs text-muted-foreground">
              Site: <span className="text-foreground">{siteName(activeSiteId)}</span>
              {flags && (
                <>
                  {" · "}Geofence radius:{" "}
                  <span className="text-foreground">{flags.time_clock_geofence_radius_m} m</span>
                </>
              )}
            </div>
          </div>
          <Switch
            checked={liveLocation}
            onCheckedChange={(v) => setLocConsent(v ? "granted" : "declined")}
            aria-label="Toggle live location sharing"
          />
        </div>
      </section>

      {/* Workspace requires the installed app for live GPS: say so plainly
          instead of offering browser sharing that a phone will freeze. */}
      {nativeGpsRequiredHere(requireNativeGps) ? (
        <NativeGpsRequiredNotice onShift={onShift} />
      ) : (
        orgId && userId && (
          <LiveLocationSelfCheck
            userId={userId}
            orgId={orgId}
            siteId={activeSiteId ?? null}
            enabled={liveLocation}
          />
        )
      )}

      {/* Guard-initiated punch requests (additive; never edits records directly) */}
      {orgId && userId && (
        <PunchRequestPanel
          orgId={orgId}
          userId={userId}
          activeEntryId={myActive?.id ?? null}
          activeSiteId={activeSiteId}
          onShift={onShift}
          geofenceRadiusM={flags?.time_clock_geofence_radius_m ?? 150}
        />
      )}

      {/* My Last Actions */}
      <section className="mt-6">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#C0C0C0]">
          My Last Actions
        </h2>
        <div className="mt-3 overflow-hidden rounded-xl border border-[#C0C0C0]/20 bg-card">
          {lastActions.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No actions yet.
            </div>
          ) : (
            <ul className="divide-y divide-[#C0C0C0]/10">
              {lastActions.map((a) => (
                <li key={a.id} className="flex items-center gap-3 px-4 py-3">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${dotTone(a.kind)}`} />
                  <span className="flex-1 text-sm text-foreground">{a.label}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {format(new Date(a.at), "MMM d · HH:mm:ss")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

function dotTone(k: Action["kind"]) {
  switch (k) {
    case "shift_start": return "bg-success";
    case "shift_end": return "bg-destructive";
    case "lunch_start":
    case "lunch_end": return "bg-[#C0C0C0]";
    case "meal_start":
    case "meal_end": return "bg-[#7CC0E8]";
  }
}

function PunchTile({
  label, sub, Icon, tone, disabled, onClick,
}: {
  label: string;
  sub: string;
  Icon: React.ComponentType<{ className?: string }>;
  tone: "success" | "danger" | "muted" | "info";
  disabled?: boolean;
  onClick: () => void;
}) {
  const toneClass = {
    success: "bg-success/15 text-success border-success/40 hover:bg-success/25",
    danger: "bg-destructive/15 text-destructive border-destructive/40 hover:bg-destructive/25",
    muted: "bg-[#2a2a2a] text-[#C0C0C0] border-[#C0C0C0]/30 hover:bg-[#333]",
    info: "bg-[#1f3a4a] text-[#7CC0E8] border-[#7CC0E8]/30 hover:bg-[#26485a]",
  }[tone];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl border-2 p-3 text-center transition-all active:scale-[0.98] ${toneClass} disabled:cursor-not-allowed disabled:bg-[#1a1a1a] disabled:text-[#555] disabled:border-[#333] disabled:hover:bg-[#1a1a1a]`}
    >
      <Icon className="h-7 w-7 sm:h-8 sm:w-8" />
      <div className="px-1">
        <div className="text-sm font-bold leading-tight sm:text-base">{label}</div>
        <div className="mt-0.5 text-[10px] uppercase tracking-[0.16em] opacity-80">{sub}</div>
      </div>
    </button>
  );
}
